import { Controller } from '@hotwired/stimulus'

// 94.2 -> "1:34", 8 -> "0:08". Announced via aria-valuetext only; the
// design shows no visible timecode.
const toClockString = (seconds) => {
  const whole = Math.floor(seconds)
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

export default class extends Controller {
  static targets = ['audio', 'button', 'label', 'playIcon', 'pauseIcon', 'range', 'waveform']

  connect () {
    // Bound refs kept in fields so disconnect() can actually remove them.
    this.onLoaded = this.onLoadedMetadata.bind(this)
    this.onTime = this.onTimeUpdate.bind(this)
    this.onEnd = this.onEnded.bind(this)
    this.onError = this.onSourceError.bind(this)
    this.onPlay = this.updatePlayButton.bind(this, true)
    this.onPause = this.updatePlayButton.bind(this, false)

    this.audioTarget.addEventListener('loadedmetadata', this.onLoaded)
    this.audioTarget.addEventListener('timeupdate', this.onTime)
    this.audioTarget.addEventListener('ended', this.onEnd)
    this.audioTarget.addEventListener('error', this.onError)

    this.audioTarget.addEventListener('play', this.onPlay)
    this.audioTarget.addEventListener('pause', this.onPause)

    // The element starts loading during HTML parse, so both of these can have
    // already happened by the time Stimulus connects and the events won't fire
    // again.
    if (this.audioTarget.error) return this.onSourceError()
    if (this.audioTarget.readyState >= 1) this.onLoadedMetadata()
  }

  disconnect () {
    this.audioTarget.pause()
    this.audioTarget.removeEventListener('loadedmetadata', this.onLoaded)
    this.audioTarget.removeEventListener('timeupdate', this.onTime)
    this.audioTarget.removeEventListener('ended', this.onEnd)
    this.audioTarget.removeEventListener('error', this.onError)
    this.audioTarget.removeEventListener('play', this.onPlay)
    this.audioTarget.removeEventListener('pause', this.onPause)
  }

  onSourceError () {
    console.error('Audio source failed to load:', this.audioTarget.currentSrc)
    this.element.hidden = true
  }

  async togglePlay () {
    if (this.audioTarget.paused) {
      try {
        await this.audioTarget.play()
      } catch (error) {
        console.error('Audio playback failed:', error)
      }
    } else {
      this.audioTarget.pause()
    }
  }

  seek () {
    this.audioTarget.currentTime = Number(this.rangeTarget.value)
  }

  // iOS only drags a range when the touch starts on its thumb, so pointer
  // scrubbing is handled here and the range is left for keyboard and AT.
  startScrub (event) {
    if (this.rangeTarget.disabled) return

    this.scrubbing = true
    this.waveformTarget.setPointerCapture(event.pointerId)
    this.seekToPointer(event)
  }

  moveScrub (event) {
    if (this.scrubbing) this.seekToPointer(event)
  }

  endScrub (event) {
    if (!this.scrubbing) return

    this.scrubbing = false
    this.waveformTarget.releasePointerCapture(event.pointerId)
  }

  seekToPointer (event) {
    const { left, width } = this.waveformTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((event.clientX - left) / width, 0), 1)

    this.audioTarget.currentTime = ratio * this.audioTarget.duration
  }

  onLoadedMetadata () {
    const { duration } = this.audioTarget
    if (!Number.isFinite(duration)) return

    this.rangeTarget.max = duration
    this.rangeTarget.disabled = false
    this.element.classList.remove('is-loading')
    this.onTimeUpdate()
  }

  onTimeUpdate () {
    const { currentTime, duration } = this.audioTarget
    if (!Number.isFinite(duration) || duration === 0) return

    this.rangeTarget.value = currentTime
    this.rangeTarget.setAttribute(
      'aria-valuetext',
      `${toClockString(currentTime)} of ${toClockString(duration)}`
    )
    this.waveformTarget.style.setProperty(
      '--progress',
      `${(currentTime / duration) * 100}%`
    )
  }

  onEnded () {
    this.audioTarget.currentTime = 0
    this.updatePlayButton(false)
    this.onTimeUpdate()
  }

  updatePlayButton (playing) {
    this.labelTarget.textContent = playing ? 'Pause' : 'Play'
    this.playIconTarget.classList.toggle('hidden', playing)
    this.pauseIconTarget.classList.toggle('hidden', !playing)
  }
}
